import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Users, MailQuestion, IndianRupee, CalendarDays,
    GraduationCap, BarChart3, Settings, LogOut, Menu, X,
} from "lucide-react";
import api from "@/lib/adminApi";
import { Logo } from "@/components/Logo";

const NAV = [
    { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/admin/students", icon: Users, label: "Students" },
    { to: "/admin/enquiries", icon: MailQuestion, label: "Enquiries" },
    { to: "/admin/payments", icon: IndianRupee, label: "Payments" },
    { to: "/admin/classes", icon: CalendarDays, label: "Classes" },
    { to: "/admin/teachers", icon: GraduationCap, label: "Teachers" },
    { to: "/admin/reports", icon: BarChart3, label: "Reports" },
    { to: "/admin/settings", icon: Settings, label: "Settings" },
];

export default function AdminLayout() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/auth/me")
            .then((r) => setUser(r.data))
            .catch(() => navigate("/admin"))
            .finally(() => setLoading(false));
    }, [navigate]);

    const logout = async () => {
        await api.post("/auth/logout").catch(() => {});
        navigate("/admin");
    };

    if (loading) {
        return (
            <div className="grid min-h-screen place-items-center bg-sand" data-testid="admin-loading">
                <p className="text-sm text-muted-foreground">Loading admin…</p>
            </div>
        );
    }

    const sidebar = (
        <div className="flex h-full flex-col">
            <div className="border-b border-border px-6 py-5"><Logo /></div>
            <nav className="flex-1 space-y-1 px-3 py-4" data-testid="admin-nav">
                {NAV.map((n) => (
                    <NavLink
                        key={n.to}
                        to={n.to}
                        onClick={() => setOpen(false)}
                        data-testid={`admin-nav-${n.label.toLowerCase()}`}
                        className={({ isActive }) =>
                            `flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                                isActive ? "bg-teal-soft text-teal" : "text-ink/70 hover:bg-sand hover:text-ink"
                            }`
                        }
                    >
                        <n.icon className="h-4.5 w-4.5 h-5 w-5" />
                        {n.label}
                    </NavLink>
                ))}
            </nav>
            <div className="border-t border-border p-4">
                <p className="truncate px-2 text-xs text-muted-foreground" data-testid="admin-user-email">{user?.email}</p>
                <button onClick={logout} data-testid="admin-logout-button"
                    className="mt-2 flex w-full items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50">
                    <LogOut className="h-5 w-5" />
                    Logout
                </button>
            </div>
        </div>
    );

    return (
        <div className="flex min-h-screen bg-sand" data-testid="admin-layout">
            <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-border bg-white lg:block">{sidebar}</aside>
            {open && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} />
                    <aside className="absolute inset-y-0 left-0 w-72 bg-white lift-shadow">{sidebar}</aside>
                </div>
            )}
            <div className="flex min-h-screen flex-1 flex-col lg:pl-64">
                <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-white/85 px-6 py-4 backdrop-blur-xl lg:justify-end">
                    <button className="grid h-10 w-10 place-items-center rounded-full border border-border lg:hidden"
                        data-testid="admin-mobile-menu-toggle" onClick={() => setOpen(!open)} aria-label="Toggle menu">
                        {open ? <X className="h-5 w-5 text-teal" /> : <Menu className="h-5 w-5 text-teal" />}
                    </button>
                    <span className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Hindi Ganga · Admin</span>
                </header>
                <main className="flex-1 px-6 py-8 lg:px-10">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
